// TODO Add object prefix to axis/series/plotLines

import * as React from "react";
import { AnyAction, Unsubscribe } from "redux";
import { useDispatch, useStore } from "react-redux";
import * as Highcharts from "highcharts/highstock";
import HighchartsReact from "highcharts-react-official";
import { Models, Services, Types, Utils } from "@sendum/core";

import { ILollipopData } from "../interfaces/ILollipopData";
import { IVisibilityState } from "../store/VisibilityReducer";
import * as VGUtils from "./VisibilityGraphsUtils";
import * as VisibilityActions from "../store/VisibilityActions";
import s from "./VisibilityGraphs.module.scss";
import { VisibilityRootState } from "../store/VisibilityStore";

Utils.setupHighcharts();

type TSensorsHistories = Map<string, Types.TProperty[]>;

const VisibilityGraphs = () => {
	const dispatch = useDispatch();
	const store = useStore();
	const storeUnsubscribe = React.useRef<Unsubscribe>();

	let tooltipPoints: any[] = [];
	let currentDeviceId = "";
	let currentSensors: string[] = [];
	const currentHistoryCounts: Map<string, number> = new Map<string, number>();
	let isCurrentLoading = false;
	const icons =
        (store.getState() as VisibilityRootState).Visibility.device?.shipment.getTimeLineAttributesConfig()
        	.icons || Utils.defaultTimelineIcons;
	let timeline: any[] = [];

	React.useEffect(
		() => () => {
			Services.EventEmitter.off("chart");
			Services.EventEmitter.off("chart_close");

			if (storeUnsubscribe.current) {
				storeUnsubscribe.current();
			}
		},
		[],
	);

	const showTooltip = (epoch: any, chart: any) => {
		if (!chart) {
			return;
		}

		if (tooltipPoints.length) {
			tooltipPoints.forEach(point => {
				point.setState("normal");
			});
			tooltipPoints = [];
		}

		chart.series.forEach((series: any) => {
			if (series.data.length !== 0) {
				const pointsAround = series.data
					.filter(
						(p: any) =>
							p["x"] >= epoch - 5 * 60 * 1000 &&
                            p["x"] <= epoch + 5 * 60 * 1000,
					)
					.map((p: any) => [Math.abs(p["x"] - epoch), p]);

				if (!!pointsAround && pointsAround.length) {
					const point = pointsAround.reduce(
						(min: any, p: any) => (min[0] > p[0] ? p : min),
						pointsAround[0],
					)[1];
					if (point) {
						tooltipPoints.push(point);
					}
				}
			}
		});

		if (tooltipPoints.length) {
			chart.tooltip.refresh(tooltipPoints);
		}

		const toolTipPlotLine = {
			color: VGUtils.readCSSColor("--sw-storm-gray-color"),
			width: 3,
			value: epoch,
			dashStyle: "ShortDot",
			zIndex: 1,
		};

		const xAxis = {
			plotLines: [
				...getPairPlotLines(
					(store.getState() as VisibilityRootState).Visibility.device,
				),
				toolTipPlotLine,
			],
			plotBands: Services.DeviceConfigService.DeviceConfig.getIsTimeLineInChart()
				? [...getTimeLinePlotBands(getTimeLine((store.getState() as VisibilityRootState).Visibility.device?.shipment))]
				: undefined,
		};

		chart.update({ xAxis: [xAxis] }, true);
	};

	const hideTooltip = (chart: any) => {
		tooltipPoints = [];
		chart.tooltip.hide(0);
	};

	const hideTooltipMap = (_epoch: any, chart: any) => {
		chart.series.forEach((series: any) => {
			series.data.forEach((point: any) => {
				if (
					Math.round(point["x"] / 1000) == Math.round(_epoch / 1000)
				) {
					point.setState("normal");
				}
			});
		});
		chart.tooltip.hide(0);
	};

	const getPairPlotLines = (device?: Models.Device): any[] => {
		if (device && device.pairHistory.length !== 0) {
			return device.pairHistory
				.filter(h => h.action === "pair")
				.map(h => ({
					color: VGUtils.readCSSColor("--sw-storm-gray-color"),
					width: 2,
					value: h.epoch,
					dashStyle: "solid",
					zIndex: 1,
					id: "pair",
					label: {
						text: h.device,
						align: "right",
						rotation: 270,
						x: -5,
						y: 20,
					},
				}));
		}

		return [];
	};

	const getTimeLinePlotBands = (timeline: any[]): any[] => {
		if (timeline && timeline.length !== 0) {
			return timeline.map(_ => ({
				id: "timeline" + _.no,
				color:
                    _.type === "Node"
                    	? "rgba(102, 178, 73, 0.1)"
                    	: "rgba(20, 79, 146, 0.1)",
				from: _.startTime,
				to: _.endTime,
				zIndex: 0,
			}));
		}

		return [];
	};

	const getTimeLine = (shipment?: Models.BaseShipment): any[] => {
		if (timeline.length) {
			return timeline;
		}
		const plotBands: any[] = [];
		if (shipment && shipment.segments.length) {
			shipment.segments.forEach((segment: Models.Segment) => {
				if (
					segment.status === "InProgress" ||
                    segment.status === "Completed"
				) {
					let startTime = 0;
					let endTime = 0;
					segment.milestones.forEach(milestone => {
						const milestoneTime =
                            milestone.actualEpochTime ||
                            milestone.scheduledEpochTime;
						if (milestoneTime) {
							if (!startTime && !endTime) {
								startTime = milestoneTime;
								endTime = milestoneTime;
							} else {
								startTime =
                                    milestoneTime < startTime
                                    	? milestoneTime
                                    	: startTime;
								endTime =
                                    milestoneTime > endTime
                                    	? milestoneTime
                                    	: endTime;
							}
						}
					});
					plotBands.push({
						startTime: startTime || segment.start,
						mode: segment.nodeMode || segment.routeMode,
						no: segment.segmentSequenceNo,
						type: segment.segmentType,
						endTime: endTime || segment.end,
					});
				}
			});

			const result: any[] = [];
			plotBands.forEach((plot, index, arr) => {
				if (index === 0) {
					plot.startTime =
                        plot.startTime ||
                        (store.getState() as VisibilityRootState).Visibility.device?.releasedDate;
				} else {
					plot.startTime = plot.startTime || arr[index - 1].endTime;
				}
				plot.endTime =
                    plot.endTime ||
                    (arr[index + 1] ? arr[index + 1].startTime : "max");

				if (!plot.endTime || !plot.startTime) {
					return;
				}
				result.push({
					...plot,
					endTime: plot.endTime === "max" ? Date.now() : plot.endTime,
					time:
                        plot.endTime === "max"
                        	? plot.startTime
                        	: plot.startTime +
                              (plot.endTime - plot.startTime) / 2,
				});
			});

			timeline = result;

			return result;
		}

		return [];
	};

	const showMapPopup = (e: any) => {
		Services.EventEmitter.emit("map", e);
	};

	const onHighchartsRefChanged = (ref: any) => {
		if (storeUnsubscribe.current) {
			storeUnsubscribe.current();
			Services.EventEmitter.off("chart");
			Services.EventEmitter.off("chart_close");
		}

		if (ref?.chart) {
			Services.EventEmitter.on("chart", epoch =>
				showTooltip(epoch, ref.chart),
			);
			Services.EventEmitter.on("chart_close", epoch =>
				hideTooltipMap(epoch, ref.chart),
			);
			ref.chart.update(
				VGUtils.getDefaultHighchartsConfig(showMapPopup),
				true,
			);
			storeUnsubscribe.current = store.subscribe(() =>
				updateGraphs(ref.chart),
			);
			updateGraphs(ref.chart);
		}
	};

	const updateGraphs = (chart: any) => {
		const state = (store.getState() as VisibilityRootState).Visibility;

		if (
			chart === null ||
            state.multiplePlaceComponent !== "graph" ||
            isDeviceChanged(state.currentDeviceId)
		) {
			return;
		}

		isLoadingHandler(state, chart);
		sensorsHandler(state, chart);
		changeHistoryHandler(state, chart);
	};

	const isDeviceChanged = (deviceId: string | undefined): boolean => {
		if (deviceId && !currentDeviceId) {
			currentDeviceId = deviceId;

			return false;
		}

		if (deviceId && currentDeviceId !== deviceId) {
			currentDeviceId = deviceId;

			return true;
		}

		return false;
	};

	const isLoadingHandler = (
		{
			device,
			sensors,
			history,
			historyLoading,
			currentDeviceId,
		}: IVisibilityState,
		chart: any,
	): void => {
		if (!device) {
			return;
		}

		const isLoading =
            sensors.length !== 0 &&
            sensors.some((sensor: string) =>
            	historyLoading.get(
            		`${device.id}#${device.shipment.id}#${sensor}`,
            	),
            );

		if (isLoading !== isCurrentLoading) {
			if (isCurrentLoading) {
				updateYAxis(device, sensors, history, chart);
				updateSeries(device, sensors, history, chart);
				chart.hideLoading();
			} else {
				chart.showLoading("Loading...");
			}
			isCurrentLoading = isLoading;
		}
	};

	const sensorsHandler = (
		{
			device,
			sensors,
			history,
			historyLoading,
			currentDeviceId,
		}: IVisibilityState,
		chart: any,
	): void => {
		if (!device) {
			return;
		}

		if (
			sensors.length !== currentSensors.length ||
            currentSensors.some(s => !sensors.includes(s))
		) {
			currentSensors = sensors;
			removeSeriesOfUnselectedSensors(sensors, chart);
			updateYAxis(device, sensors, history, chart);
			const load = loadSensorsHistory(
				device,
				sensors,
				history,
				historyLoading,
				chart,
			);
			if (!load) {
				updateSeries(device, sensors, history, chart);
			}
		}
	};

	const changeHistoryHandler = (
		{ device, history }: IVisibilityState,
		chart: any,
	): void => {
		if (!device) {
			return;
		}

		let needUpdateSeries = false;

		[...history.keys()].forEach(key => {
			const sensorName = key.split("#")[2];
			if (currentSensors.includes(sensorName)) {
				const sensorHistoryCount = history.get(key)?.length;
				if (
					!!sensorHistoryCount &&
                    !!currentHistoryCounts.get(sensorName) &&
                    currentHistoryCounts.get(sensorName) !== sensorHistoryCount
				) {
					needUpdateSeries = true;
				}
			}
		});

		if (needUpdateSeries) {
			updateSeries(device, currentSensors, history, chart, true);
		}
	};

	const loadSensorsHistory = (
		device: Models.Device,
		sensors: string[],
		history: TSensorsHistories,
		historyLoading: Map<string, boolean>,
		chart: any,
	): boolean => {
		let load = false;

		sensors.forEach((sensor: string) => {
			if (sensor === "setTempRange") {
				return;
			}
			const deviceHistory = history.get(
				`${device.id}#${device.shipment.id}#${sensor}`,
			);
			const isSensorLoading = historyLoading.get(
				`${device.id}#${device.shipment.id}#${sensor}`,
			);
			if (!deviceHistory && !isSensorLoading) {
				dispatch(VisibilityActions.getHistoryAction(sensor) as unknown as AnyAction);
				chart.showLoading("Loading...");
				load = true;
			}
		});

		return load;
	};

	const removeSeriesOfUnselectedSensors = (
		sensors: string[],
		chart: any,
	): void => {
		chart.series.forEach((s: any) => {
			if (s.userOptions.id === "highcharts-navigator-series") {
				return;
			}

			if (!sensors.includes(s.userOptions.id)) {
				if (
					s.userOptions.id === "line-doorOpen" &&
                    sensors.includes("doorOpen")
				) {
					return;
				}
				const series = chart.get(s.userOptions.id);
				if (series) {
					series.remove(false);
				}
			}

			if (!sensors.includes("doorOpen")) {
				const series = chart.get("line-doorOpen");
				if (series) {
					series.remove(false);
				}
			}
		});
	};

	const updateYAxis = (
		device: Models.Device,
		sensors: string[],
		history: TSensorsHistories,
		chart: any,
	): void => {
		let yAxis: any[] = VGUtils.getYAxis(device, sensors);
		const yAxisIds: string[] = sensors.map(sensorName =>
			VGUtils.getYAxisId(device, sensorName),
		);

		if (yAxisIds.includes("Temperature")) {
			setupTemperatureAxis(
				device,
				sensors,
				history,
				yAxis.find((item: any) => item.id === "Temperature"),
			);
		}
		if (
			yAxisIds.includes("Temperature") &&
            (yAxisIds.includes("Humidity") || yAxisIds.includes("Battery"))
		) {
			if (yAxisIds.includes("Humidity") && yAxisIds.includes("Battery")) {
				const yH: any = yAxis.find(
					(item: any) => item.id === "Humidity",
				);
				const yB: any = yAxis.find(
					(item: any) => item.id === "Battery",
				);
				if (yH) {
					yH.opposite = true;
					yH.title.y = -15;
					yH.labels.align = "right";
					yH.title.textAlign = "right";
					yH.useHTML = true;
					yH.title.text = `${yH.title.text}<br/>${yB?.title.text}`;
				}
			} else {
				const yH: any = yAxis.find(
					(item: any) => item.id === "Humidity",
				);
				if (yH) {
					yH.opposite = true;
					yH.labels.align = "right";
					yH.title.textAlign = "right";
					yH.title.y = 0;
				}

				const yB: any = yAxis.find(
					(item: any) => item.id === "Battery",
				);
				if (yB) {
					yB.opposite = true;
					yB.labels.align = "right";
					yB.title.textAlign = "right";
				}
			}
		} else if (
			yAxisIds.includes("Humidity") &&
            yAxisIds.includes("Battery")
		) {
			const yH: any = yAxis.find((item: any) => item.id === "Humidity");
			if (yH) {
				yH.title.y = 0;
			}

			const yB: any = yAxis.find((item: any) => item.id === "Battery");
			if (yB) {
				yB.opposite = true;
				yB.labels.align = "right";
				yB.title.textAlign = "right";
			}
		}

		if (
			yAxisIds.includes("Temperature_C") &&
            (yAxisIds.includes("%") || yAxisIds.includes("dBm"))
		) {
			const yH: any = yAxis.find((item: any) => item.id === "%");
			if (yH) {
				yH.labels.align = "right";
				yH.title.textAlign = "right";
				yH.opposite = true;
			}

			const yB: any = yAxis.find((item: any) => item.id === "dBm");
			if (yB) {
				yB.labels.align = "right";
				yB.title.textAlign = "right";
				yB.opposite = true;
			}
		} else if (yAxisIds.includes("%") && yAxisIds.includes("Pa")) {
			const yH: any = yAxis.find((item: any) => item.id === "%");
			if (yH) {
				yH.opposite = false;
			}

			const yB: any = yAxis.find((item: any) => item.id === "dBm");
			if (yB) {
				yB.opposite = true;
				yB.labels.align = "right";
				yB.title.textAlign = "right";
			}
		}

		yAxis = yAxis.map((axis: any) => {
			axis.visible = true;
			axis.showEmpty = true;

			return axis;
		});

		const currentYAxis = chart.yAxis
			.map((y: any) => y.userOptions.id)
			.filter(
				(id: string) =>
					![
						"highcharts-navigator-yaxis",
						"navigator-y-axis",
					].includes(id),
			);

		currentYAxis.forEach((id: string) => {
			const axis = yAxis.find(y => y.id === id);
			if (!axis) {
				chart.get(id).remove(false);
			}
		});

		yAxis.forEach(y => {
			if (!currentYAxis.includes(y.id)) {
				chart.addAxis(y, false, false);
			} else {
				chart.get(y.id).update(y, false);

				if (y.id === "Temperature") {
					if (y.plotLines) {
						chart.get(y.id).addPlotLine(y.plotLines);
					} else {
						chart.get(y.id).removePlotLine("temperaturePlotLines");
					}
					if (y.plotBands) {
						chart.get(y.id).addPlotBand(y.plotBands);
					} else {
						chart.get(y.id).removePlotBand("temperaturePlotBand");
					}
				}
			}
		});

		chart.redraw();

		if (sensors.length === 1 && sensors[0] === "setTempRange") {
			const series = {
				id: "hiddenSeries",
				data: [],
				showInLegend: false,
				visible: false,
				yAxis: "Temperature",
			};
			chart.addSeries(series, true);
		}
	};

	const updateSeries = (
		device: Models.Device,
		sensors: string[],
		history: TSensorsHistories,
		chart: any,
		noScale?: boolean,
	) => {
		const extremes: any[] = [];
		if (noScale) {
			chart.xAxis.forEach((x: any) => {
				extremes.push({ ...x.getExtremes() });
			});
		}

		const histories: Map<string, number[][] | ILollipopData[]> = new Map();
		const pairDates = device.pairHistory.map(h => h.epoch);
		let xMin: number = Math.min(...pairDates);
		let xMax: number = Math.max(...pairDates);

		if (sensors.length) {
			sensors.forEach(sensorName => {
				const sensorData = VGUtils.getHistoryBySensorName(
					device,
					history,
					sensorName,
				);
				currentHistoryCounts.set(sensorName, sensorData.length);

				const min = Math.min(...sensorData.map(d => d[0]));
				if (xMin === null || min < xMin) {
					xMin = min;
				}
				const max = Math.max(...sensorData.map(d => d[0]));
				if (xMax === null || max > xMax) {
					xMax = max;
				}

				if (sensorName !== "setTempRange") {
					const trueValue = VGUtils.getSensorBooleanValue(
						device,
						sensorName,
						true,
					);
					const falseValue = VGUtils.getSensorBooleanValue(
						device,
						sensorName,
						false,
					);
					if (sensorName === "doorOpen") {
						const filterData = sensorData.filter((d, i, arr) => {
							if (!i) {
								return d[1] !== 0;
							}

							return arr[i - 1][1] !== d[1] && d[1];
						});
						const data: ILollipopData[] = filterData.map(a => ({
							x: a[0],
							low: 1,
							y: 1,
							name: a[1] ? trueValue : falseValue,
						}));
						histories.set(sensorName, data);
					} else if (
						VGUtils.getSensorDataType(device, sensorName) ===
                        "boolean"
					) {
						const data: ILollipopData[] = sensorData.map(a => ({
							x: a[0],
							y: a[1],
							name: a[1] ? trueValue : falseValue,
						}));
						histories.set(sensorName, data);
					} else {
						histories.set(sensorName, sensorData);
					}
				}
			});
		}

		hideTooltip(chart);
		const currentSeriesIds = chart.series.map((s: any) => s.userOptions.id);
		const series = buildSeries(device, histories);
		if (Services.DeviceConfigService.DeviceConfig.getIsTimeLineInChart()) {
			series.push({
				id: "timeline",
				sensorName: "timeline",
				unit: "",
				name: "",
				useHTML: true,
				dataLabels: {
					useHTML: true,
				},
				data: getTimeLine(
					(store.getState() as VisibilityRootState).Visibility.device?.shipment,
				).map(_ => ({
					x: _.time,
					text: "",
					title: `<div style="width:24px; height:24px; border-radius:5px; background-color: ${
						_.type === "Node"
							? VGUtils.readCSSColor("--sw-accent-color").trim()
							: VGUtils.readCSSColor(
								"--sw-brand-color-900",
							).trim()
					}"><img src="${icons.get(
						_.mode,
					)}"  style="width:14px; height:14px; margin:5px"/></div>`,
					shape: VGUtils.white,
				})),
				y: -26,
				width: 16,
				title: "",
				yAxis: "hidden",
				type: "flags",
				showInLegend: false,
				tooltip: {
					headerFormat:
                        '<span style="font-size: 10px">{point.x:%Custom}</span><br/>',
					pointFormat:
                        '<span style="color:{point.color}; margin-right: 2px">●</span><b>{point.y}</b><br/>',
					valuePrefix: " ",
					valueSuffix: " ",
					shared: true,
				},
				showInNavigator: false,
			});
		}

		series.forEach((s: any) => {
			if (!currentSeriesIds.includes(s.id)) {
				chart.addSeries(s, false);
			} else {
				chart.get(s.id).update({ data: s.data }, false);
			}
		});

		if (Math.abs(xMin) === Infinity) {
			xMin = device.releasedDate || new Date().getTime() - 60 * 60 * 1000;
		}
		if (Math.abs(xMax) === Infinity) {
			xMax = new Date().getTime();
		}

		xMin = xMin - 5 * 60 * 1000;
		xMax = xMax + 5 * 60 * 1000;

		chart.update(
			{
				xAxis: [
					{
						plotLines: getPairPlotLines(device),
						plotBands: Services.DeviceConfigService.DeviceConfig.getIsTimeLineInChart()
							? getTimeLinePlotBands(
								getTimeLine((store.getState() as VisibilityRootState).Visibility.device?.shipment),
							)
							: undefined,
						min: series.length === 0 ? undefined : xMin,
						max: series.length === 0 ? undefined : xMax,
					},
				],
				navigator: {
					xAxis: {
						min: series.length === 0 ? undefined : xMin,
						max: series.length === 0 ? undefined : xMax,
					},
				},
			},
			false,
		);

		chart.get("mainXAxis").setTitle(
			{
				text: series.length !== 0 ? "Timezone: UTC" : "",
				align: "middle",
			},
			false,
		);

		chart.redraw();

		if (!noScale) {
			chart.xAxis.forEach((x: any) => {
				if (series.length === 0) {
					x.setExtremes();
				} else {
					x.setExtremes(xMin, xMax);
				}
			});
		} else {
			chart.xAxis.forEach((x: any, index: number) => {
				if (extremes[index]) {
					x.setExtremes(extremes[index].min, extremes[index].max);
				}
			});
		}
		chart.redraw();
	};

	const buildSeries = (
		device: Models.Device,
		histories: Map<string, number[][] | ILollipopData[]>,
	): any[] => {
		const series: any[] = [];

		[...histories.keys()].forEach(sensorName => {
			const history = histories.get(sensorName);
			const sensorHistory =
                !history || history.length === 0 ? [] : history;
			const unit = VGUtils.getUnit(device, sensorName);
			const seriesName = VGUtils.getSeriesName(device, sensorName);
			const yAxisId = VGUtils.getYAxisId(device, sensorName);

			if (sensorHistory) {
				series.push({
					id: sensorName,
					sensorName,
					unit,
					name: unit ? `${seriesName}(${unit})` : seriesName,
					data: sensorHistory,
					dataGrouping: {
						approximation:
                            sensorName === "doorOpen" ? "sum" : "average",
						enabled: !(
							VGUtils.getSensorDataType(device, sensorName) ===
                            "boolean"
						),
					},
					yAxis: yAxisId,
					turboThreshold: 10000,
					type: VGUtils.getChartType(device, sensorName),
					linkedTo:
                        sensorName === "doorOpen" ? "line-doorOpen" : undefined,
					marker: {
						symbol:
                            sensorName === "doorOpen"
                            	? VGUtils.doorOpenMarker
                            	: "circle",
						enabled: true,
						radius: 2,
					},
					showInLegend: !(sensorName === "doorOpen"),
					color: VGUtils.getChartColor(device, sensorName),
					gapSize: 301000,
					gapUnit: "value",
					tooltip: {
						headerFormat:
                            '<span style="font-size: 10px">{point.x:%Custom}</span><br/>',
						pointFormat:
                            VGUtils.getSensorDataType(device, sensorName) ===
                            "boolean"
                            	? '<span style="color:{point.color}; margin-right: 2px">●<b>{series.name}: </b></span><b>{point.name}</b><br/>'
                            	: '<span style="color:{point.color}; margin-right: 2px">●</span><b>{point.y}</b><br/>',
						valuePrefix: seriesName + ": ",
						valueSuffix: " " + unit,
						shared: true,
					},
					showInNavigator: !(sensorName === "doorOpen"),
				});

				if (sensorName === "doorOpen") {
					series.push({
						id: "line-doorOpen",
						seriesName,
						name: seriesName,
						unit,
						data: [],
						yAxis: "hidden",
						marker: {
							symbol: VGUtils.doorOpenMarker,
							width: 9,
							height: 14,
							enabled: true,
						},
						showInNavigator: false,
					});
				}
			}
		});

		return series;
	};

	const setupTemperatureAxis = (
		device: Models.Device,
		sensors: string[],
		history: TSensorsHistories,
		axis: any,
	) => {
		let tempMax: number | null = null;
		let tempMin: number | null = null;
		let min: number | null = null;
		let max: number | null = null;
		let tempRange: number | null = null;

		if (
			device?.type === "ENVIROTAINER" &&
            sensors.includes("setTempRange")
		) {
			const present: Models.Envirotainer.EnvirotainerState = device.present as Models.Envirotainer.EnvirotainerState;
			if (present?.setTempRange) {
				tempRange = +present.setTempRange.value;
				if (
					+present.setTempRange.value >= 2 &&
                    +present.setTempRange.value <= 8
				) {
					tempMax = 8;
					tempMin = 2;
				} else if (
					+present.setTempRange.value >= 15 &&
                    +present.setTempRange.value <= 25
				) {
					tempMax = 25;
					tempMin = 15;
				}
			}
		}

		if (sensors.length) {
			sensors.forEach(sensorName => {
				const sensorData = VGUtils.getHistoryBySensorName(
					device,
					history,
					sensorName,
				);
				if (
					VGUtils.getYAxisId(device, sensorName) === "Temperature" &&
                    sensorData.length
				) {
					const min_t = Math.min(...sensorData.map(a => a[1]));
					const max_t = Math.max(...sensorData.map(a => a[1]));

					min = min !== null && min < min_t ? min : min_t;
					max = max !== null && max > max_t ? max : max_t;
				}
			});
		}

		if (sensors.includes("setTempRange")) {
			axis.plotLines = getTemperaturePlotLines(tempRange);
			axis.plotBands = getTemperaturePlotBands(tempMin, tempMax);
		}
		axis.min = getYMin(tempMin, tempMax, min, max, 0);
		axis.max = getYMax(tempMin, tempMax, min, max, 30);
	};

	const getTemperaturePlotBands = (
		tempMin: number | null,
		tempMax: number | null,
	): any[] => {
		if (tempMax !== null && tempMin !== null) {
			return [
				{
					id: "temperaturePlotBand",
					color: VGUtils.readCSSColor("--sw-shaded-grey"),
					from: tempMin,
					to: tempMax,
					zIndex: 0,
				},
			];
		}

		return [];
	};

	const getTemperaturePlotLines = (tempRange: number | null): any[] => {
		if (tempRange !== null) {
			return [
				{
					id: "temperaturePlotLines",
					name: "Set Temp",
					color: VGUtils.readCSSColor("--sw-storm-gray-color"),
					width: 3,
					value: tempRange,
					dashStyle: "ShortDot",
					zIndex: 1,
				},
			];
		}

		return [];
	};

	const getYMin = (
		tempMin: number | null,
		tempMax: number | null,
		min: number | null,
		max: number | null,
		_default: number,
	): number => {
		if (tempMax !== null && tempMin !== null) {
			return (min !== null && tempMin > min ? min : tempMin) - 1;
		}
		if (max && min) {
			return min - (max - min) * 0.05;
		}

		return _default;
	};

	const getYMax = (
		tempMin: number | null,
		tempMax: number | null,
		min: number | null,
		max: number | null,
		_default: number,
	): number => {
		if (tempMax !== null && tempMin !== null) {
			return (max !== null && tempMax < max ? max : tempMax) + 1;
		}
		if (max && min) {
			return max + (max - min) * 0.05;
		}

		return _default;
	};

	return (
		<div className={s.graph}>
			<HighchartsReact
				ref={onHighchartsRefChanged}
				highcharts={Highcharts}
				options={VGUtils.getDefaultHighchartsConfig(showMapPopup)}
				constructorType="stockChart"
			/>
		</div>
	);
};

export default VisibilityGraphs;
